
import { computed, defineComponent, reactive } from 'vue';
import { IOption } from '@/core/models/AntDesign/IOption';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeSistema from '@/store/storeSistema';
import ServicoVeiculoMarcaModelo from '@/servicos/Cadastros/Veiculo/ServicoVeiculoMarcaModelo';

export default defineComponent({
  name: 'SelecionaMarcaModelo',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: Number || undefined,
    },
    descricaoSelecionada: {
      type: String,
      default: '',
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    empresas: {
      type: Array as () => number[],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Digite para selecionar',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'update:descricaoSelecionada', 'change'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapida } = useTelaBase();

    let debounce = 0;
    const servicoVeiculoMarcaModelo = new ServicoVeiculoMarcaModelo();

    const state = reactive({
      listaMarcaModelo: [] as IOption[],
      buscandoDados: false,
      emDigitacao: false,
    });

    async function obterCorPorCodigo(valor: any) {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.valorValido(valor)) { return; }
      if (!(valor > 0)) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else if (props.codigoSelecionado === 0) {
        return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaMarcaModelo)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaMarcaModelo.map((c) => c.value);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaMarcaModelo.find((c) => c.value === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }
      }

      if (!efetuarConsulta) return;

      state.listaMarcaModelo = [];

      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        apenasAtivos: false, recursoAssociado: '',
      };

      if (UtilitarioGeral.validaLista(props.empresas)) {
        parametrosConsultaRapida.empresas = props.empresas;
      } else {
        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
      }

      if (props.varios) {
        parametrosConsultaRapida.filtrarPorVariosCodigos = true;
        parametrosConsultaRapida.valores = valor;
      } else {
        parametrosConsultaRapida.filtrarPorCodigo = true;
        parametrosConsultaRapida.valor = valor;
      }

      const listaMarcaModelo = await servicoVeiculoMarcaModelo.consultaRapida(parametrosConsultaRapida);
      state.listaMarcaModelo = montaOpcoesComListaConsultaRapida(listaMarcaModelo);
      if (!UtilitarioGeral.validaLista(listaMarcaModelo)) {
        if (props.varios) {
          emit('update:codigosSelecionados', undefined);
        } else {
          emit('update:codigoSelecionado', undefined);
        }
      }
    }

    async function pesquisarCor(valorPesquisa: any) {
      state.emDigitacao = true;

      state.buscandoDados = true;
      state.listaMarcaModelo = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '',
        };

        if (UtilitarioGeral.validaLista(props.empresas)) {
          parametrosConsultaRapida.empresas = props.empresas;
        } else {
          parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
        }

        const listaMarcaModelo = await servicoVeiculoMarcaModelo.consultaRapida(parametrosConsultaRapida);
        state.buscandoDados = false;
        state.listaMarcaModelo = montaOpcoesComListaConsultaRapida(listaMarcaModelo);
      }, 600);
    }

    function obterCor():any {
      obterCorPorCodigo(props.codigoSelecionado);
      return (props.codigoSelecionado === 0 ? undefined : props.codigoSelecionado);
    }

    const computedDescricaoSelecionada = computed({
      get: () => props.descricaoSelecionada,
      set: (val: string) => {
        emit('update:descricaoSelecionada', val);
      },
    });

    const computedCodigoSelecionado = computed({
      get: () => obterCor(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
        const option = state.listaMarcaModelo.find((c) => c.value === val);
        if (option !== undefined) {
          computedDescricaoSelecionada.value = option.label;
        }
      },
    });

    function obterCors():number[] {
      obterCorPorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterCors(),
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    function change() {
      emit('change', props.varios ? props.codigosSelecionados : props.codigoSelecionado);
    }

    return {
      props,
      state,
      pesquisarCor,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
      computedDescricaoSelecionada,
      change,
    };
  },
});
